import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Cellcount, Diffcount, Rci as RCI } from "../../../../../../fakeDb";
import utils from "../../utils";
import preferences from "../../../../../../fakeDb/diagnostics/references";
import calculateIndicators from "../../../../../calculateIndicators";

pdfMake.vfs = pdfFonts?.pdfMake?.vfs;

const devGroups = {
  adult: [
    "Young Adult",
    "Adult",
    "Middle Aged",
    "Senior",
    "Elderly",
    "Geriatric",
  ],
  child: ["Child", "Pre-Teen", "Teenager"],
  infant: ["Toddler", "Infant"],
  neonate: ["Neonatal", "Fetal"],
};

const parseValue = (value) =>
  value <= 2 ? value.toFixed(2) : value < 10 ? value.toFixed(1) : value;

function convertSuperscriptHTML(unit) {
  if (!unit) return "";
  const supMap = {
    0: "⁰",
    1: "¹",
    2: "²",
    3: "³",
    4: "⁴",
    5: "⁵",
    6: "⁶",
    7: "⁷",
    8: "⁸",
    9: "⁹",
  };

  return unit.replace(/<sup>(\d+)<\/sup>/g, (_, digits) =>
    digits
      .split("")
      .map((d) => supMap[d] || d)
      .join("")
  );
}

export const Hematology = async ({ task, form, result }) => {
  const { patient, cc, dc, rci, apc, troupe, signatories } = task;
  const { dob } = patient;
  const { Abbreviation, Title, Si } = Cellcount;
  const isMale = patient.isMale ? "Male" : "Female";
  const imageBase64 = await utils.getImage(result);

  const tableSection = (titleRow, rows) => [
    [
      { text: titleRow[0], bold: true, fontSize: 12.5 },
      { text: titleRow[1], bold: true, fontSize: 12.5, alignment: "center" },
      { text: titleRow[2], bold: true, fontSize: 12.5 },
    ],
    ...rows,
  ];

  const cellCountRows = cc.map((cell, index) => {
    const devString = preferences.getDevelopmentByBirthDate(dob).name;
    const development =
      Object.entries(devGroups).find(([, arr]) =>
        arr.includes(devString)
      )?.[0] || "neonate";
    const gender = isMale ? "Male" : "Female";

    const cellRef =
      development === "adult"
        ? Si.cells[Abbreviation[index]][development][gender]
        : Si.cells[Abbreviation[index]][development];

    const { lo, hi, unit } = cellRef || {};

    const color = cell < lo ? "blue" : cell > hi && "red",
      numCell = Number(cell),
      indicators = calculateIndicators(
        cellRef,
        !isNaN(numCell) ? numCell.toFixed(numCell < 20 ? 2 : 0) : ""
      );

    return [
      { text: Title[index] || "" },
      {
        text: `${numCell.toFixed(cell < 20 ? 2 : 0)} ${indicators || ""}`,
        alignment: "center",
        bold: true,
        color,
      },
      {
        text: `${parseValue(lo)} - ${parseValue(hi)} ${convertSuperscriptHTML(
          unit
        )}`,
      },
    ];
  });

  cellCountRows.push([
    "APC",
    {
      text: apc?.toString(),
      alignment: "center",
      bold: true,
      color: apc < 150 ? "blue" : apc > 450 ? "red" : undefined,
    },
    "150 - 450 ⁹/L",
  ]);

  const diffCountRows = Object.values(dc).map((val, idx) => {
    const { Category } = Diffcount,
      { Conventionals } = Cellcount;

    const devString = preferences.getDevelopmentByBirthDate(dob).name;
    const development =
      Object.entries(devGroups).find(([, arr]) =>
        arr.includes(devString)
      )?.[0] || "neonate";

    const category = Category[idx],
      { lo, hi } = Conventionals.differentials[category][development],
      color = val < lo ? "blue" : val > hi && "red";

    return [
      category,
      {
        text: val ? (val / 100).toFixed(2) : "",
        alignment: "center",
        bold: true,
        color,
      },
      `${(lo / 100).toFixed(2)} - ${(hi / 100).toFixed(2)}`,
    ];
  });

  const rciRows = rci.map((val, idx) => {
    const category = RCI.Category[idx];
    const { lo, hi, unit } = Cellcount?.Preferences?.rci[category];
    const color = val < lo ? "blue" : val > hi ? "red" : undefined;
    return [
      category,
      { text: val.toString(), alignment: "center", bold: true, color },
      `${lo} - ${hi} ${unit}`,
    ];
  });

  rciRows.push([
    "Bleeding Time",
    {
      text: troupe?.bt?.[0]
        ? `${troupe.bt[0]} min : ${["00", "15", "30", "45"][troupe.bt[1]]} sec.`
        : "",
      alignment: "center",
    },
    "2 - 4 mins",
  ]);
  rciRows.push([
    "Clotting Time",
    {
      text: troupe?.ct?.[0]
        ? `${troupe.ct[0]} min : ${["00", "15", "30", "45"][troupe.ct[1]]} sec.`
        : "",
      alignment: "center",
    },
    "2 - 4 mins",
  ]);
  rciRows.push([
    "Reticulocytes",
    { text: troupe?.retic > 0 ? `${troupe.retic}` : "", alignment: "center" },
    "0.5 - 1.5%",
  ]);
  rciRows.push([
    "ESR",
    { text: troupe?.esr > 0 ? `${troupe.esr}` : "", alignment: "center" },
    "0 - 15 mm/hr",
  ]);

  const [head, dr] = signatories;
  const headSig = await utils.getSignature(head.email);
  const drSig = await utils.getSignature(dr.email);
  const imgLogo = await utils.getLogo();
  const QrCode = await utils.generateQrCODE(result);

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [10, 59, 10, 60],

    background: (_, pageSize) => [
      {
        image: imageBase64,
        width: pageSize.width,
        height: 50,
        absolutePosition: { x: 0, y: 0 },
      },
    ],

    content: [
      // ✅ Full-width colored section header
      ...utils.header(task),
      {
        table: {
          widths: ["*"],
          body: [
            [
              {
                text: "H  E  M  A  T  O  L  O  G  Y",
                alignment: "center",
                characterSpacing: 2,
                fontSize: 15,
                bold: true,
                margin: [0, 0, 0, 0],
              },
            ],
          ],
        },
        layout: {
          fillColor: () => utils.formColor(form.toLowerCase()),
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 2,
          paddingBottom: () => 2,
          hLineColor: () => "#ccc",
          vLineColor: () => "#ccc",
        },
        margin: [0, 0, 0, 1],
      },

      // ✅ Keep this table section as-is (your CBC/Diff/RCI table)
      {
        style: "tableSpacing",
        table: {
          widths: ["*", 120, 140],
          body: [
            ...tableSection(
              ["Complete Blood Count", "Results", "Reference"],
              cellCountRows
            ),
            ...tableSection(
              ["Differential Count", "Results", "Reference"],
              diffCountRows
            ),
            ...tableSection(
              ["Red Cell Immunohaematology", "Results", "Reference"],
              rciRows
            ),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#aaa",
          vLineColor: () => "#aaa",
        },
      },
    ],
    footer: utils.footer({ task, drSig, headSig, imgLogo, QrCode }),

    styles: {
      footerName: {
        fontSize: 11,
        bold: true,
      },
      footerTitle: {
        fontSize: 8,
        italics: true,
      },
      header: {
        fontSize: 16,
        bold: true,
        alignment: "center",
      },
      sectionHeader: {
        bold: true,
        fillColor: "#eee",
        fontSize: 13,
        margin: [0, 5],
      },
      tableSpacing: {
        margin: [0, 10, 0, 20],
      },
    },
    defaultStyle: {
      fontSize: 10,
    },
  };
  utils.download(docDefinition, "Hematology");
};
export default Hematology;
